import type { UserAPI } from '@sydle/ui-api';
import { APIEvents, defineAPIs, getAPI } from '@sydle/ui-api';
import '@sydle/ui-utils';
import { Provider, navigate } from '@sydle/ui-utils';
import { createPortalAPI } from '../api';
import { getOrganizationConfig } from '../models/serviceDeskConfig';
import { pushAlert } from '../utils/alert.utils';
import { OneAPIHelper } from '../utils/api/one.api';
import { DialogHelper } from '../utils/dialog.utils';
import { login, logout } from '../utils/root.utils';
import { RouterHelper } from '../utils/router-utils';
import { setGlobalTokens } from '../utils/theme.utils';
import { getLanguage, translation } from '../utils/translation.utils';
import { URLHelper } from '../utils/url.utils';
import appStore from './app.store';

export default async (): Promise<void> => {
  defineAPIs(); // TODO remove when one-components externalize lib
  createPortalAPI();
  setGlobalTokens();

  // URL params
  const forceAuth = URLHelper.getURLParam('forceAuth') === 'true';

  if (forceAuth && !OneAPIHelper.isLoggedIn()) {
    const orgConfig = await getOrganizationConfig();
    if (RouterHelper.isAuthRoute('currentRoute')) return;

    const hasExternalType =
      orgConfig?.authenticatorsConfig?.length === 1 &&
      orgConfig?.authenticatorsConfig[0].type !== 'basic' &&
      orgConfig?.authenticatorsConfig[0].type !== 'certificate';

    if (hasExternalType) {
      URLHelper.deleteURLParam('forceAuth');
      window.stop();
      window.location.href =
        orgConfig?.authenticatorsConfig[0].loginUrl + '?one_redirect=' + encodeURIComponent(window.location.href);

      document.body?.querySelector('sy-app-root')?.remove();

      return;
    }

    await DialogHelper.open({
      header: {},
      content: {
        slotComponent: 'sy-sd-dialog-login',
      },
      footer: {},
      hideFooter: true,
    });
  } else {
    URLHelper.deleteURLParam('forceAuth');
  }

  // Listeners
  window.onstorage = async (event: StorageEvent) => {
    if (event.key !== 'isLoggedIn') {
      return;
    }

    (document?.body?.querySelectorAll('sy-dialog') || []).forEach((dialog: HTMLSyDialogElement) => {
      dialog.close();
    });

    if (event.newValue === 'true') {
      await login();
    } else {
      await logout();
    }
  };

  window.addEventListener('message', (event: MessageEvent) => {
    if (!event.data || event.data === 'recaptcha-setup') {
      return;
    }

    let data: { subject: string; body: { [key: string]: any } } | undefined;
    try {
      data = JSON.parse(event.data);
    } catch (e) {
      console.warn('Fail parsing the received message: ', e);
    }

    if (data?.subject !== 'redirect' || !data?.body) {
      return;
    }

    if (data.body.route) {
      navigate(data.body.route);
    } else if (data.body.url) {
      window.location.href = data.body.url;
    }
  });

  document.addEventListener(APIEvents.UNAUTHORIZED, async () => {
    if (OneAPIHelper.isLoggedIn()) {
      await logoutExpiredSession();
    }
  });

  document.addEventListener(APIEvents.INVALID_TOKEN, async () => {
    await logoutExpiredSession();
  });

  document.addEventListener(APIEvents.EXPIRING_SESSION, (event: CustomEvent) => {
    if (!appStore.get('hasLogoutTimeoutId')) {
      pushAlert({
        text: translation('SESSION_EXPIRE_SOON', 'Your session will expire soon, please log in again.'),
        type: 'INFO',
      });
      const timeToExpire =
        event.detail.remaingSessionTimeout - 20 > 0
          ? event.detail.remaingSessionTimeout - 20
          : event.detail.remaingSessionTimeout;
      const timeoutId = setTimeout(async () => {
        const userAPI = getAPI<UserAPI>('userAPI');
        if (userAPI?.remainingSessionTimeout() && userAPI.remainingSessionTimeout() < 20) {
          await logoutExpiredSession();
        }
      }, timeToExpire * 1000);

      appStore.set('hasLogoutTimeoutId', true);
      appStore.set('logoutTimeoutId', timeoutId);
    }
  });

  const bodyElement = document.body;
  if (!bodyElement) {
    console.warn('Document body not found.');
    return;
  }

  bodyElement.addEventListener('sySdRecoverPasswordDidChange', async () => {
    navigate('home');

    DialogHelper.open({
      header: {},
      content: {
        slotComponent: 'sy-sd-dialog-login',
      },
      footer: {},
      hideFooter: true,
    });
  });

  bodyElement.addEventListener('sySdAuthDidLogin', async () => {
    await login();
  });

  bodyElement.addEventListener('sySdNavbarDidLogout', async () => {
    await logout();
    pushAlert({ text: translation('LOG_OUT_MESSAGE', 'Your session was ended.'), type: 'INFO' });
  });

  bodyElement.addEventListener('sySdUserMenuDidLogout', async () => {
    await logout();
    pushAlert({ text: translation('LOG_OUT_MESSAGE', 'Your session was ended.'), type: 'INFO' });
  });

  // Contexts
  const bodyProvider = new Provider(bodyElement);
  bodyProvider.provides('language', getLanguage());

  bodyElement.addEventListener('sySDLanguageDidChange', (e: CustomEvent<string>) => {
    if (!e.detail) {
      return;
    }

    bodyProvider.update('language', e.detail);
  });
};

async function logoutExpiredSession() {
  await logout();
  pushAlert({
    text: translation('SESSION_HAS_EXPIRED', 'Your session has expired, please log in again.'),
    type: 'INFO',
  });
}
